/**
 * observationCriteriaCompletion.js
 *
 * Pure, tested helpers that determine whether Behavioural Observation Criterion
 * Mapping is complete. These power sidebar badge colours, progress fractions,
 * and the soft gate before Review (SSI-style warning dialog — clinicians may
 * still Proceed Anyway).
 *
 * Redesign (criteria-by-criteria)
 * -------------------------------
 * Evidence decisions are now stored once per DSM criterion on
 * `observation.data.criteriaEvidence` (e.g. `{ A1: 'Clear evidence', ... }`),
 * rather than once per task × criterion. Completion is therefore "have all of
 * A1–B4 been decided?", independent of which tasks were administered.
 *
 * Legacy (task-by-task) helpers below are retained so older assessments that
 * only have per-task `dsm_<id>_evidence` fields still resume / progress
 * correctly until they are remapped.
 *
 * Tasks marked "Not administered" are excluded from every *legacy* aggregate
 * helper (via `getAdministeredTaskIds`).
 */

import {
  getAdministeredTaskIds,
  isObservationTaskAdministered,
} from './observationTaskAdministration';

/** DSM A1–B4 — the criteria that require an evidence decision in Criterion Mapping. */
const OBS_EVIDENCE_CRITERION_IDS = ['A1', 'A2', 'A3', 'B1', 'B2', 'B3', 'B4'];

/**
 * How many DSM criteria carry an evidence decision in Criterion Mapping — the
 * denominator for the v2 pro-rata Criterion Mapping progress third.
 */
export const OBS_EVIDENCE_CRITERION_COUNT = OBS_EVIDENCE_CRITERION_IDS.length;

/**
 * Counts how many of the DSM criteria (A1–B4) have an evidence decision on the
 * criteria-level map. Used for the v2 pro-rata progress third so the dashboard
 * pill creeps up as each criterion is decided (rather than jumping from 0 to a
 * full third only once all seven are done).
 *
 * @param {object|null|undefined} criteriaEvidence
 * @returns {number} 0..OBS_EVIDENCE_CRITERION_COUNT
 */
export function countDecidedCriteriaEvidence(criteriaEvidence) {
  if (!criteriaEvidence || typeof criteriaEvidence !== 'object') return 0;
  return OBS_EVIDENCE_CRITERION_IDS.filter((id) => Boolean(criteriaEvidence[id])).length;
}

/**
 * True when every DSM criterion (A1–B4) has an evidence decision on the
 * criteria-level map introduced by the Criterion Mapping redesign.
 *
 * @param {object|null|undefined} criteriaEvidence
 * @returns {boolean}
 */
export function areAllCriteriaEvidenceComplete(criteriaEvidence) {
  if (!criteriaEvidence || typeof criteriaEvidence !== 'object') return false;
  return OBS_EVIDENCE_CRITERION_IDS.every((id) => Boolean(criteriaEvidence[id]));
}

/**
 * True when at least one criteria-level evidence decision exists.
 *
 * @param {object|null|undefined} criteriaEvidence
 * @returns {boolean}
 */
export function hasAnyCriteriaEvidenceDecision(criteriaEvidence) {
  if (!criteriaEvidence || typeof criteriaEvidence !== 'object') return false;
  return OBS_EVIDENCE_CRITERION_IDS.some((id) => Boolean(criteriaEvidence[id]));
}

/**
 * Lists DSM criteria still missing an evidence decision — feeds the soft-gate
 * warning dialog on Criterion Mapping → Review.
 *
 * @param {object|null|undefined} criteriaEvidence
 * @returns {Array<{ criterionId: string, shortTitle: string }>}
 */
export function listIncompleteObservationCriteria(criteriaEvidence) {
  const evidence = criteriaEvidence && typeof criteriaEvidence === 'object'
    ? criteriaEvidence
    : {};
  // Importing DSM5_DOMAINS here would create a circular-ish path through the
  // observation config barrel; short titles are derived from the id map below.
  const SHORT_TITLES = {
    A1: 'Social-emotional reciprocity',
    A2: 'Nonverbal communication',
    A3: 'Relationships',
    B1: 'Stereotyped behaviour/speech',
    B2: 'Insistence on sameness',
    B3: 'Restricted interests',
    B4: 'Sensory reactivity',
  };
  return OBS_EVIDENCE_CRITERION_IDS
    .filter((id) => !evidence[id])
    .map((id) => ({
      criterionId: id,
      shortTitle: SHORT_TITLES[id] || id,
    }));
}

/**
 * Defaults + clinician-added extras for one task, de-duplicated.
 * @param {Object} taskConfig
 * @param {Object} [taskData]
 * @returns {string[]}
 */
export function getObservationTaskCriterionIds(taskConfig, taskData) {
  if (!taskConfig) return [];
  const extraCriteria = taskData?.dsm_extraCriteria || [];
  const defaultCriteria = taskConfig.relevantCriteria || [];
  return [
    ...defaultCriteria,
    ...extraCriteria.filter((id) => !defaultCriteria.includes(id)),
  ];
}

/**
 * Returns true when a single task has an evidence decision recorded for every
 * one of its criteria (defaults + extras). A task with no criteria at all
 * (neither default nor extra) is considered "not complete" — it means the
 * clinician has not yet engaged with criteria mapping for that task.
 *
 * Legacy helper — prefer areAllCriteriaEvidenceComplete for new assessments.
 *
 * @param {Object} taskConfig - The task definition from ADULT_ASD_OBSERVATION_TASKS
 * @param {Object} taskData - The persisted data for this task (allTaskData[taskId])
 * @returns {boolean}
 */
export function isObservationTaskCriteriaComplete(taskConfig, taskData) {
  if (!taskConfig || !taskData) return false;

  const allCriteria = getObservationTaskCriterionIds(taskConfig, taskData);
  if (allCriteria.length === 0) return false;

  return allCriteria.every((criterionId) => Boolean(taskData[`dsm_${criterionId}_evidence`]));
}

/**
 * Returns true when Criterion Mapping is complete.
 *
 * Prefers the criteria-level `criteriaEvidence` map when any decision has been
 * recorded there (new redesign). Falls back to the legacy per-task check so
 * in-flight assessments that only have task-level evidence still complete.
 *
 * @param {number[]} selectedTaskIds
 * @param {Object[]} tasks
 * @param {Object} allTaskData
 * @param {object|null|undefined} [criteriaEvidence]
 * @returns {boolean}
 */
export function areAllObservationCriteriaComplete(
  selectedTaskIds,
  tasks,
  allTaskData,
  criteriaEvidence = null,
) {
  // New path: once the clinician has started recording criteria-level
  // decisions, completeness is defined solely by A1–B4 on that map.
  if (hasAnyCriteriaEvidenceDecision(criteriaEvidence)) {
    return areAllCriteriaEvidenceComplete(criteriaEvidence);
  }

  if (!Array.isArray(selectedTaskIds) || selectedTaskIds.length === 0) return false;
  if (!allTaskData) return false;

  // Legacy path: only administered tasks need their criteria decided.
  const administeredIds = getAdministeredTaskIds(selectedTaskIds, allTaskData);
  if (administeredIds.length === 0) return false;

  return administeredIds.every((taskId) => {
    const taskConfig = tasks.find((t) => t.id === taskId);
    if (!taskConfig) return false;
    return isObservationTaskCriteriaComplete(taskConfig, allTaskData[taskId] || {});
  });
}

/**
 * True when at least one evidence decision has been recorded (criteria-level
 * or legacy per-task). Gates Criteria paragraph generation.
 *
 * @param {number[]} selectedTaskIds
 * @param {Object[]} tasks
 * @param {Object} allTaskData
 * @param {object|null|undefined} [criteriaEvidence]
 * @returns {boolean}
 */
export function hasAnyObservationCriterionDecision(
  selectedTaskIds,
  tasks,
  allTaskData,
  criteriaEvidence = null,
) {
  if (hasAnyCriteriaEvidenceDecision(criteriaEvidence)) return true;

  if (!Array.isArray(selectedTaskIds) || selectedTaskIds.length === 0) return false;
  if (!allTaskData) return false;

  const administeredIds = getAdministeredTaskIds(selectedTaskIds, allTaskData);

  return administeredIds.some((taskId) => {
    const taskConfig = tasks?.find((t) => t.id === taskId);
    if (!taskConfig) return false;
    const taskData = allTaskData[taskId] || {};
    return getObservationTaskCriterionIds(taskConfig, taskData)
      .some((criterionId) => Boolean(taskData[`dsm_${criterionId}_evidence`]));
  });
}

/**
 * Lists selected tasks that still need one or more evidence decisions — used
 * by the legacy Criterion Mapping → Review warning dialog.
 *
 * Prefer listIncompleteObservationCriteria for the redesigned flow.
 *
 * @param {number[]} selectedTaskIds
 * @param {Object[]} tasks
 * @param {Object} allTaskData
 * @returns {Array<{ taskId: number, shortTitle: string, displayNumber: number, missingCriteria: string[] }>}
 */
export function listIncompleteObservationCriteriaTasks(selectedTaskIds, tasks, allTaskData) {
  if (!Array.isArray(selectedTaskIds) || selectedTaskIds.length === 0) return [];

  const incomplete = [];
  // Iterate the full array so displayNumber stays positional (index + 1); a
  // task marked "Not administered" is simply skipped rather than filtered out,
  // which would renumber every task after it.
  selectedTaskIds.forEach((taskId, index) => {
    const taskConfig = tasks?.find((t) => t.id === taskId);
    if (!taskConfig) return;
    const taskData = allTaskData?.[taskId] || {};
    if (!isObservationTaskAdministered(taskData)) return;
    const criterionIds = getObservationTaskCriterionIds(taskConfig, taskData);
    const missingCriteria = criterionIds.length === 0
      ? [] // no criteria configured yet — still treat as incomplete
      : criterionIds.filter((id) => !taskData[`dsm_${id}_evidence`]);

    if (criterionIds.length === 0 || missingCriteria.length > 0) {
      incomplete.push({
        taskId,
        shortTitle: taskConfig.shortTitle || `Task ${index + 1}`,
        displayNumber: index + 1,
        missingCriteria,
      });
    }
  });
  return incomplete;
}
